import React, { useEffect, useState } from "react";
import SelectInput from "../Components/SelectInput";
import {
  getDocDataSnap,
  setDocData,
  queryAllDocsByField,
} from "../../../utils/firebase/Firebase.utils";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { doc } from "firebase/firestore";
import InputBox from "../Components/InputBox";
import ScheduledMaintenance from "./ScheduledMaintenance";

// Import react-chartjs-2 components for creating charts
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function ItemDetails({ item, setViewMode }) {
  const [formData, setFormData] = useState(item);
  const [isProcessing, setIsProcessing] = useState(false);
  const [updated, setUpdated] = useState(true);
  const [frequency, setFrequency] = useState("");
  const [activeTab, setActiveTab] = useState("details");
  const [maintenanceHistory, setMaintenanceHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);

  const currentUser = useSelector((state) => state.userObject);

  useEffect(() => {
    const fetchItem = async () => {
      const data = await getDocDataSnap("maintenanceItems", item.id, (data) => {
        setFormData(data);
      });
    };
    fetchItem();
  }, []);

  useEffect(() => {
    const fetchMaintenanceHistory = async () => {
      try {
        setIsLoadingHistory(true);
        const historyData = await queryAllDocsByField(
          "completedMaintenance",
          "maintenanceId",
          "==",
          item.id
        );
        setMaintenanceHistory(historyData || []);
      } catch (error) {
        console.error("Error fetching maintenance history:", error);
        toast.error("Failed to load maintenance history");
      } finally {
        setIsLoadingHistory(false);
      }
    };

    fetchMaintenanceHistory();
  }, [item.id]);

  const frequencies = [
    "Daily",
    "Weekly",
    "Bi-Weekly",
    "Monthly",
    "Quarterly",
    "Semi-Annual",
    "Yearly",
  ];
  const frequenciesOptions = frequencies.map((item) => {
    return {
      value: item,
      label: item,
    };
  });

  const handleSelectFrequency = (selected) => {
    setUpdated(false);
    setFrequency(selected.value);
  };

  const handleUpdate = async () => {
    setIsProcessing(true);
    const docObject = {
      ...formData,
      frequency: frequency,
      updatedBy: currentUser.uid,
      updatedName: currentUser.displayName,
      updatedEmail: currentUser.email,
      updatedAt: new Date().toLocaleString(),
      updateType: "Maintenance Frequency",
    };
    await setDocData("maintenanceItems", item.id, docObject);
    toast.success("Item Updated Successfully");
    setUpdated(true);
    setIsProcessing(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    if (formData.status === "Scheduled") {
      toast.error(
        "Item is already scheduled for maintenance, Edit the schedule instead"
      );
      setIsProcessing(false);
      return;
    }

    const docObject = {
      ...formData,
      nextSchedule: formData.nextSchedule,
      budget: formData.budget,
      supplier: formData.supplier,
      status: "Scheduled",
      scheduledBy: currentUser.uid,
      scheduledName: currentUser.displayName,
      scheduledEmail: currentUser.email,
      updateType: "Maintenance Schedule Set",
      scheduledAt: new Date().toLocaleString(),
    };
    await setDocData("maintenanceItems", item.id, docObject);
    toast.success("Maintenance Schedule Set Successfully");
    setIsProcessing(false);
  };

  return (
    <div className="bg-white w-full min-h-screen rounded-xl shadow-lg relative overflow-hidden">
      {/* Top Header Bar */}
      <div className="border-b border-gray-200 text-[#363062] p-4 flex justify-between items-center">
        <div className="flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2 text-[#363062]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
          <h1 className="text-lg font-semibold tracking-wide">
            {formData.name}
          </h1>
        </div>
        <button
          className="flex items-center text-white bg-[#363062] hover:bg-opacity-90 rounded-lg px-3 py-1.5 text-sm transition-all duration-200"
          onClick={() => setViewMode(false)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
          Close
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 px-4">
        <button
          className={`px-4 py-3 text-sm font-medium flex items-center transition-all duration-200 ${
            activeTab === "details"
              ? "text-[#363062] border-b-2 border-[#8FCD00]"
              : "text-gray-500 hover:text-[#363062]"
          }`}
          onClick={() => setActiveTab("details")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Details
        </button>
        <button
          className={`px-4 py-3 text-sm font-medium flex items-center transition-all duration-200 ${
            activeTab === "schedule"
              ? "text-[#363062] border-b-2 border-[#8FCD00]"
              : "text-gray-500 hover:text-[#363062]"
          }`}
          onClick={() => setActiveTab("schedule")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          Schedule
        </button>
        <button
          className={`px-4 py-3 text-sm font-medium flex items-center transition-all duration-200 ${
            activeTab === "history"
              ? "text-[#363062] border-b-2 border-[#8FCD00]"
              : "text-gray-500 hover:text-[#363062]"
          }`}
          onClick={() => setActiveTab("history")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          History
        </button>
        <button
          className={`px-4 py-3 text-sm font-medium flex items-center transition-all duration-200 ${
            activeTab === "trends"
              ? "text-[#363062] border-b-2 border-[#8FCD00]"
              : "text-gray-500 hover:text-[#363062]"
          }`}
          onClick={() => setActiveTab("trends")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
            />
          </svg>
          Cost Trends
        </button>
      </div>

      {/* Main Content Area */}
      <div className="p-6 flex flex-col lg:flex-row gap-6">
        {/* Left Column */}
        <div className="w-full lg:w-1/3">
          {activeTab === "details" && (
            <>
              {/* Item Quick Info */}
              <div className="flex gap-4 mb-6">
                <div className="w-1/2 border border-gray-200 rounded-lg p-4">
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    Frequency
                  </div>
                  <div className="flex items-center mt-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-1.5 text-[#363062]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span className="font-medium text-[#363062]">
                      {formData.frequency || "Not Set"}
                    </span>
                  </div>
                </div>
                <div className="w-1/2 border border-gray-200 rounded-lg p-4">
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    Location
                  </div>
                  <div className="flex items-center mt-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-1.5 text-[#363062]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <span className="font-medium text-[#363062]">
                      {formData.location}
                    </span>
                  </div>
                </div>
              </div>

              {/* Item Details Card */}
              <div className="border border-gray-200 rounded-lg p-5 mb-6">
                <div className="flex items-center mb-4 pb-2 border-b border-gray-100">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-[#363062] mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <h3 className="text-sm font-semibold text-[#363062]">
                    Item Details
                  </h3>
                </div>

                <div className="space-y-3 text-sm">
                  <div className="flex items-center py-2 border-b border-gray-100">
                    <span className="w-1/2 font-medium text-gray-600">
                      Description
                    </span>
                    <span className="w-1/2 text-gray-800">
                      {formData.description}
                    </span>
                  </div>

                  <div className="flex items-center py-2 border-b border-gray-100">
                    <span className="w-1/2 font-medium text-gray-600">
                      Last Maintenance
                    </span>
                    <span className="w-1/2 text-gray-800">
                      {formData.lastMaintenanceSchedule || "Not Set"}
                    </span>
                  </div>

                  <div className="flex items-center py-2 border-b border-gray-100">
                    <span className="w-1/2 font-medium text-gray-600">
                      Last Cost
                    </span>
                    <span className="w-1/2 text-gray-800">
                      {formData.lastMaintenanceCost || "Not Set"}
                    </span>
                  </div>

                  <div className="flex items-center py-2 border-b border-gray-100">
                    <span className="w-1/2 font-medium text-gray-600">
                      Last Supplier
                    </span>
                    <span className="w-1/2 text-gray-800">
                      {formData.lastMaintenanceSupplier || "Not Set"}
                    </span>
                  </div>

                  <div className="flex items-center py-2">
                    <span className="w-1/2 font-medium text-gray-600">
                      Next Schedule
                    </span>
                    <span className="w-1/2 text-gray-800">
                      {formData.nextSchedule || "Not Set"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Frequency Update Card */}
              <div className="border border-gray-200 rounded-lg p-5">
                <div className="flex items-center mb-4 pb-2 border-b border-gray-100">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-[#363062] mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <h3 className="text-sm font-semibold text-[#363062]">
                    Update Maintenance Frequency
                  </h3>
                </div>

                <div>
                  <SelectInput
                    options={frequenciesOptions}
                    placeholder="Select Frequency"
                    onChange={(selected) => handleSelectFrequency(selected)}
                    name="frequency"
                  />
                  <button
                    className="w-full mt-3 bg-[#8FCD00] hover:bg-opacity-90 text-white py-2.5 rounded-lg text-sm font-medium transition-all duration-200 disabled:bg-opacity-50 disabled:cursor-not-allowed flex justify-center items-center"
                    onClick={handleUpdate}
                    disabled={isProcessing || updated}
                  >
                    {isProcessing ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Updating...
                      </>
                    ) : (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 mr-1.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                        Update Frequency
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}

          {activeTab === "schedule" && (
            <div className="border border-gray-200 rounded-lg p-5">
              <div className="flex items-center mb-4 pb-2 border-b border-gray-100">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-[#363062] mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <h3 className="text-sm font-semibold text-[#363062]">
                  Schedule Maintenance
                </h3>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <InputBox
                    name="nextSchedule"
                    label="Next Schedule"
                    type="date"
                    placeholder="Enter next schedule"
                    onChange={handleChange}
                    required
                    className="pl-10"
                  />
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <InputBox
                    name="budget"
                    label="Budget"
                    type="number"
                    placeholder="Enter Budget"
                    onChange={handleChange}
                    required
                    className="pl-10"
                  />
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                  <InputBox
                    name="supplier"
                    label="Assigned Supplier"
                    type="text"
                    placeholder="Enter Assigned Supplier"
                    onChange={handleChange}
                    required
                    className="pl-10"
                  />
                </div>

                <button
                  className="w-full bg-[#363062] hover:bg-opacity-90 text-white py-2.5 rounded-lg text-sm font-medium transition-all duration-200 disabled:bg-opacity-50 disabled:cursor-not-allowed flex justify-center items-center"
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 mr-1.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      Schedule Maintenance
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {activeTab === "history" && (
            <div className="border border-gray-200 rounded-lg p-5">
              <div className="flex items-center mb-4 pb-2 border-b border-gray-100">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-[#363062] mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <h3 className="text-sm font-semibold text-[#363062]">
                  Maintenance History Filters
                </h3>
              </div>

              <div className="space-y-4">
                <InputBox
                  name="fromDate"
                  label="From Date"
                  type="date"
                  onChange={() => {}}
                />
                <InputBox
                  name="toDate"
                  label="To Date"
                  type="date"
                  onChange={() => {}}
                />
                <button className="w-full mt-2 bg-[#8FCD00] hover:bg-opacity-90 text-white py-2 rounded-lg text-sm font-medium transition-all duration-200">
                  Apply Filters
                </button>
              </div>
            </div>
          )}

          {activeTab === "trends" && (
            <div className="border border-gray-200 rounded-lg p-5">
              <div className="flex items-center mb-4 pb-2 border-b border-gray-100">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-[#363062] mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
                  />
                </svg>
                <h3 className="text-sm font-semibold text-[#363062]">
                  Chart Information
                </h3>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-2">
                    How to Use
                  </h4>
                  <p className="text-xs text-gray-600">
                    The cost trends chart visualizes your maintenance spending
                    over time. Use the controls in the chart area to:
                  </p>
                  <ul className="mt-2 space-y-1 text-xs text-gray-600 list-disc pl-5">
                    <li>Toggle between line and bar charts</li>
                    <li>Change the time range displayed</li>
                    <li>Compare actual costs with budgeted amounts</li>
                  </ul>
                </div>

                <div className="pt-3 border-t border-gray-100">
                  <h4 className="font-medium text-sm text-gray-700 mb-2">
                    Tips
                  </h4>
                  <ul className="space-y-1 text-xs text-gray-600 list-disc pl-5">
                    <li>
                      Bar charts are better for comparing individual values
                    </li>
                    <li>Line charts show trends over time more clearly</li>
                    <li>The cost summary shows your key metrics at a glance</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="w-full lg:w-2/3">
          <div className="border border-gray-200 rounded-lg p-5 h-full">
            {activeTab === "details" || activeTab === "schedule" ? (
              <>
                <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100">
                  <div className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-[#363062] mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                    <h3 className="text-sm font-semibold text-[#363062]">
                      Current Scheduled Maintenance
                    </h3>
                  </div>
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs border ${
                      formData.status === "Scheduled"
                        ? "border-green-200 text-green-800"
                        : "border-gray-200 text-gray-800"
                    }`}
                  >
                    {formData.status || "Not Scheduled"}
                  </span>
                </div>

                {formData.status === "Scheduled" ? (
                  <ScheduledMaintenance item={formData} id={item.id} />
                ) : (
                  <div className="flex flex-col justify-center items-center h-64 border border-dashed border-gray-200 rounded-lg">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-12 w-12 text-gray-300 mb-3"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">
                      No Maintenance Scheduled
                    </h4>
                    <p className="text-xs text-gray-500 mb-4">
                      Schedule maintenance using the form
                    </p>
                    <button
                      className="px-4 py-2 border border-[#363062] text-[#363062] hover:bg-[#363062] hover:text-white text-xs rounded-lg transition-all duration-200 flex items-center"
                      onClick={() => setActiveTab("schedule")}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3.5 w-3.5 mr-1.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                      Schedule Now
                    </button>
                  </div>
                )}
              </>
            ) : activeTab === "history" ? (
              <>
                <div className="flex items-center mb-4 pb-2 border-b border-gray-100">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-[#363062] mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <h3 className="text-sm font-semibold text-[#363062]">
                    Maintenance History Records
                  </h3>
                </div>

                <MaintenanceHistoryList itemId={item.id} />
              </>
            ) : (
              <>
                <div className="flex items-center mb-4 pb-2 border-b border-gray-100">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-[#363062] mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
                    />
                  </svg>
                  <h3 className="text-sm font-semibold text-[#363062]">
                    Maintenance Cost Trends
                  </h3>
                </div>

                <CostTrendChart
                  maintenanceHistory={maintenanceHistory}
                  isLoading={isLoadingHistory}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// New Component for Maintenance History List
function MaintenanceHistoryList({ itemId }) {
  const [maintenanceHistory, setMaintenanceHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);

  useEffect(() => {
    const fetchMaintenanceHistory = async () => {
      try {
        setIsLoadingHistory(true);
        const historyData = await queryAllDocsByField(
          "completedMaintenance",
          "maintenanceId",
          "==",
          itemId
        );
        setMaintenanceHistory(historyData || []);
      } catch (error) {
        console.error("Error fetching maintenance history:", error);
        toast.error("Failed to load maintenance history");
      } finally {
        setIsLoadingHistory(false);
      }
    };

    fetchMaintenanceHistory();
  }, [itemId]);

  if (isLoadingHistory) {
    return (
      <div className="flex justify-center items-center p-10">
        <svg
          className="animate-spin h-8 w-8 text-[#363062]"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    );
  }

  if (maintenanceHistory.length === 0) {
    return (
      <div className="text-center p-10 border border-dashed border-gray-200 rounded-lg">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-12 w-12 text-gray-300 mx-auto mb-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h4 className="text-sm font-medium text-gray-700 mb-1">
          No Maintenance History
        </h4>
        <p className="text-xs text-gray-500">
          This item has no previous maintenance records
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5 overflow-y-auto max-h-[70vh]">
      {maintenanceHistory.map((record, index) => (
        <div
          key={index}
          className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all"
        >
          <div className="flex justify-between items-center pb-3 mb-3 border-b border-gray-100">
            <div className="flex items-center">
              <span className="px-2.5 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                Completed
              </span>
              <span className="ml-3 text-sm text-gray-700">
                {record.completedAt}
              </span>
            </div>
            <span className="text-xs text-gray-500">
              by {record.completedName}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                Actual Cost
              </div>
              <div className="font-medium text-gray-800 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-1.5 text-gray-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {record.actualCost || record.budget || "N/A"}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                Supplier
              </div>
              <div className="font-medium text-gray-800 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-1.5 text-gray-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
                {record.actualSupplier || record.supplier || "N/A"}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                Date
              </div>
              <div className="font-medium text-gray-800 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-1.5 text-gray-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                {record.completedAt}
              </div>
            </div>
          </div>

          {/* Documents */}
          {record.documents && record.documents.length > 0 && (
            <div className="mt-3">
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                Documents
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {record.documents.map((doc, idx) => (
                  <div
                    key={idx}
                    className="border rounded-lg p-2 flex justify-between items-center"
                  >
                    <div>
                      <span className="text-xs font-medium text-gray-700">
                        {doc.type}
                      </span>
                      <p className="text-xs text-gray-500 truncate w-32">
                        {doc.name}
                      </p>
                    </div>
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline"
                    >
                      View
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Comments */}
          {record.comments && record.comments.length > 0 && (
            <div className="mt-4 pt-3 border-t border-gray-100">
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                Comments
              </div>
              <div className="space-y-2">
                {record.comments.slice(0, 2).map((comment, idx) => (
                  <div
                    key={idx}
                    className="text-xs text-gray-700 border-l-2 border-gray-200 pl-3 py-1"
                  >
                    <div className="flex justify-between">
                      <span className="font-medium">{comment.createdBy}</span>
                      <span className="text-gray-500">{comment.createdAt}</span>
                    </div>
                    <p className="mt-1">{comment.message}</p>
                  </div>
                ))}
                {record.comments.length > 2 && (
                  <div className="text-xs text-blue-600 hover:underline cursor-pointer">
                    + {record.comments.length - 2} more comments
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// New Component for Cost Trend Visualization
function CostTrendChart({ maintenanceHistory, isLoading }) {
  const [chartType, setChartType] = useState("line");
  const [timeRange, setTimeRange] = useState("all");
  const [compareWithBudget, setCompareWithBudget] = useState(true);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-10">
        <svg
          className="animate-spin h-8 w-8 text-[#363062]"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    );
  }

  if (maintenanceHistory.length === 0) {
    return (
      <div className="text-center p-10 border border-dashed border-gray-200 rounded-lg">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-12 w-12 text-gray-300 mx-auto mb-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1}
            d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
          />
        </svg>
        <h4 className="text-sm font-medium text-gray-700 mb-1">
          No Maintenance Cost Data
        </h4>
        <p className="text-xs text-gray-500">
          Complete some maintenance tasks to see cost trends
        </p>
      </div>
    );
  }

  // Filter data based on time range
  const filterDataByTimeRange = (data) => {
    if (timeRange === "all") return data;

    const now = new Date();
    const cutoffDate = new Date();

    if (timeRange === "year") {
      cutoffDate.setFullYear(now.getFullYear() - 1);
    } else if (timeRange === "6months") {
      cutoffDate.setMonth(now.getMonth() - 6);
    }

    return data.filter((record) => new Date(record.completedAt) >= cutoffDate);
  };

  // Sort history by date and prepare data for chart
  const filteredHistory = filterDataByTimeRange([...maintenanceHistory]);
  const sortedHistory = [...filteredHistory].sort((a, b) => {
    return new Date(a.completedAt) - new Date(b.completedAt);
  });

  // Extract dates and costs
  const labels = sortedHistory.map((record) => {
    const date = new Date(record.completedAt);
    return date.toLocaleDateString(undefined, {
      month: "short",
      year: "numeric",
    });
  });

  const actualCosts = sortedHistory.map((record) =>
    parseFloat(record.actualCost || record.budget || 0)
  );

  const budgetCosts = sortedHistory.map((record) =>
    parseFloat(record.budget || 0)
  );

  // Calculate average cost
  const averageCost =
    actualCosts.length > 0
      ? actualCosts.reduce((sum, cost) => sum + cost, 0) / actualCosts.length
      : 0;

  // Chart data
  const data = {
    labels,
    datasets: [
      {
        label: "Actual Cost",
        data: actualCosts,
        borderColor: "#363062",
        backgroundColor: "rgba(54, 48, 98, 0.5)",
        tension: 0.1,
      },
      ...(compareWithBudget
        ? [
            {
              label: "Budgeted Cost",
              data: budgetCosts,
              borderColor: "#8FCD00",
              backgroundColor: "rgba(143, 205, 0, 0.5)",
              tension: 0.1,
            },
          ]
        : []),
    ],
  };

  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Maintenance Cost History",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Cost",
        },
      },
    },
  };

  const handleTimeRangeChange = (selected) => {
    if (selected && selected.value) {
      setTimeRange(selected.value);
    }
  };

  return (
    <div>
      <div className="mb-6 border border-gray-200 rounded-lg p-4">
        <div className="flex flex-wrap gap-4 justify-between items-center">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Chart Type
            </label>
            <div className="flex gap-2">
              <button
                className={`px-3 py-1.5 text-xs border border-[#363062] rounded-lg transition-all duration-200 ${
                  chartType === "line"
                    ? "bg-[#363062] text-white"
                    : "text-[#363062] hover:bg-[#363062] hover:text-white"
                }`}
                onClick={() => setChartType("line")}
              >
                Line Chart
              </button>
              <button
                className={`px-3 py-1.5 text-xs border border-[#363062] rounded-lg transition-all duration-200 ${
                  chartType === "bar"
                    ? "bg-[#363062] text-white"
                    : "text-[#363062] hover:bg-[#363062] hover:text-white"
                }`}
                onClick={() => setChartType("bar")}
              >
                Bar Chart
              </button>
            </div>
          </div>

          <div className="w-48">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Time Range
            </label>
            <SelectInput
              options={[
                { value: "all", label: "All Time" },
                { value: "year", label: "Last Year" },
                { value: "6months", label: "Last 6 Months" },
              ]}
              placeholder="Select Range"
              onChange={handleTimeRangeChange}
              defaultValue={{ value: "all", label: "All Time" }}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Compare with Budget
            </label>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="compareBudget"
                checked={compareWithBudget}
                onChange={(e) => setCompareWithBudget(e.target.checked)}
                className="h-4 w-4 text-[#363062] focus:ring-[#363062] border-gray-300 rounded"
              />
              <label
                htmlFor="compareBudget"
                className="ml-2 text-xs text-gray-600"
              >
                Show Budget vs. Actual
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="h-[400px]">
        {chartType === "line" ? (
          <Line data={data} options={options} />
        ) : (
          <Bar data={data} options={options} />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Cost Summary
          </h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-xs text-gray-600">Average Cost:</span>
              <span className="text-xs font-medium">
                {averageCost.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-gray-600">Total Spent:</span>
              <span className="text-xs font-medium">
                {actualCosts.reduce((sum, cost) => sum + cost, 0).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-gray-600">Highest Cost:</span>
              <span className="text-xs font-medium">
                {actualCosts.length > 0
                  ? Math.max(...actualCosts).toFixed(2)
                  : "0.00"}
              </span>
            </div>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Maintenance Count
          </h4>
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-3xl font-bold text-[#363062]">
                {filteredHistory.length}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {timeRange === "all"
                  ? "Total Maintenance Records"
                  : timeRange === "year"
                  ? "Maintenance Records (Last Year)"
                  : "Maintenance Records (Last 6 Months)"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
